import { useState } from 'react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ToggleField from '@/components/Indicators/ToggleField'
import SliderField from '@/components/Indicators/SliderField'
import { defaultConfig } from './index'

// Únicos ajustes visibles (I1.4 del spec): toggles de display + LTFs.
// Modo Suave/Estricto y footprint real NO se exponen en esta iteración.
const _LTF_OPTIONS = ['1m', '5m', '15m', '30m', '1h']

// Campos del modelo de salida (I3, Spec_Orderflow_Indicador_I3_Stats.md
// §4.1/§5): se editan en un borrador LOCAL y solo se aplican (dispara el
// fetch a /stats) al cerrar el diálogo o pulsar "Aplicar" -- nunca por
// keystroke, para no generar una tormenta de requests mientras se arrastra
// un slider. `null` = "usa el default resuelto por el backend" (congelado
// en el paso 0, condicional a la resolución del gráfico para tp_r) -- se
// rellena con el valor EFECTIVO devuelto por el endpoint tras el primer
// fetch (ver ChartPublicPage.jsx), nunca queda un default implícito sin
// mostrar.
const _MODEL_BUDGET_KEYS = ['slBufferAtr', 'tp1R', 'tp2R', 'tp3R', 'budgetUsdt', 'riskPct', 'leverage']

function LtfSelect({ label, value, onChange, tooltip }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-600 dark:text-gray-300" title={tooltip}>{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="text-xs rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5"
      >
        {_LTF_OPTIONS.map(tf => (
          <option key={tf} value={tf}>{tf}</option>
        ))}
      </select>
    </div>
  )
}

export default function OrderflowQuantumPanel({ config, onChange, onClose }) {
  const cfg = { ...defaultConfig, ...(config || {}) }
  const update = (key, value) => onChange({ ...cfg, [key]: value })
  const reset = () => onChange({ ...defaultConfig })

  // Borrador local solo para MODELO DE SALIDA / BUDGET -- el resto de
  // campos (LTFs, toggles de display) siguen aplicándose de inmediato como
  // ya hacían, sin cambiar su comportamiento existente.
  const [draft, setDraft] = useState(() => {
    const d = {}
    for (const k of _MODEL_BUDGET_KEYS) d[k] = cfg[k]
    return d
  })
  const updateDraft = (key, value) => setDraft(prev => ({ ...prev, [key]: value }))
  const apply = () => onChange({ ...cfg, ...draft })
  const applyAndClose = () => { apply(); onClose() }

  return (
    <IndicatorConfigModal title="Configuración Orderflow Quantum" isOpen onClose={applyAndClose} onReset={reset}>
      <div className="space-y-3">
        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
            Data Source
          </div>
          <LtfSelect
            label="OrderFlow LTF"
            value={cfg.orderflowLtf}
            onChange={v => update('orderflowLtf', v)}
            tooltip="Resolución con la que se trocea cada vela para las burbujas de OrderFlow."
          />
          <LtfSelect
            label="Swing LTF"
            value={cfg.swingLtf}
            onChange={v => update('swingLtf', v)}
            tooltip="Resolución que usa la señal para medir la agresión dentro de la mecha. Si no es menor que el timeframe del gráfico, cae automáticamente a OrderFlow LTF."
          />
        </div>

        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
            Display
          </div>
          <ToggleField label="OrderFlow (burbujas)" value={cfg.showBubbles} onChange={v => update('showBubbles', v)} />
          <ToggleField label="Mostrar niveles SL/TP en el chart" value={cfg.showLevels !== false} onChange={v => update('showLevels', v)} />
        </div>

        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
            Signals
          </div>
          <ToggleField label="Long" value={cfg.showLong} onChange={v => update('showLong', v)} />
          <ToggleField label="Short" value={cfg.showShort} onChange={v => update('showShort', v)} />
        </div>

        <div className="space-y-2 pt-1 border-t border-slate-200 dark:border-gray-700">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
            Modelo de salida (simulación)
          </div>
          <SliderField
            label="Buffer SL (× ATR)"
            value={draft.slBufferAtr ?? cfg.slBufferAtr ?? 0.5}
            min={0.05} max={2.0} step={0.05} unit="×"
            onChange={v => updateDraft('slBufferAtr', v)}
          />
          <SliderField
            label="TP1 (× R)"
            value={draft.tp1R ?? cfg.tp1R ?? 1.5}
            min={0.5} max={10} step={0.1} unit="R"
            onChange={v => updateDraft('tp1R', v)}
          />
          <SliderField
            label="TP2 (× R)"
            value={draft.tp2R ?? cfg.tp2R ?? 2.0}
            min={0.5} max={10} step={0.1} unit="R"
            onChange={v => updateDraft('tp2R', v)}
          />
          <SliderField
            label="TP3 (× R)"
            value={draft.tp3R ?? cfg.tp3R ?? 3.0}
            min={0.5} max={10} step={0.1} unit="R"
            onChange={v => updateDraft('tp3R', v)}
          />
          <p className="text-[10px] text-slate-500 dark:text-gray-400 leading-relaxed">
            Defaults extraídos del estudio F1 (corrida sin SL real).
          </p>
        </div>

        <div className="space-y-2 pt-1 border-t border-slate-200 dark:border-gray-700">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
            Budget (simulación)
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-600 dark:text-gray-300">Budget (USDT)</span>
            <input
              type="number" min={10} max={1000000} step="any"
              value={draft.budgetUsdt ?? cfg.budgetUsdt ?? 1000}
              onChange={e => {
                const n = Number(e.target.value)
                if (Number.isFinite(n)) updateDraft('budgetUsdt', n)
              }}
              className="w-24 px-2 py-1 text-[11px] rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          <SliderField
            label="Riesgo por trade"
            value={draft.riskPct ?? cfg.riskPct ?? 10}
            min={1} max={100} step={1} unit="%"
            onChange={v => updateDraft('riskPct', v)}
          />
          <SliderField
            label="Apalancamiento"
            value={draft.leverage ?? cfg.leverage ?? 6}
            min={1} max={20} step={1} unit="×"
            onChange={v => updateDraft('leverage', v)}
          />
        </div>

        <button
          onClick={apply}
          className="w-full py-1.5 text-[11px] font-medium rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          Aplicar
        </button>
      </div>
    </IndicatorConfigModal>
  )
}
